/**
 * Meta (D58): our Facebook and Instagram ads are measured with the Meta Pixel (a page view per
 * visit) and the game server's Conversions API events (server/src/meta.ts), which carry the ids
 * in Meta's cookies: `_fbc`, the click id of the ad a player came from (`fbclid` in the link), and
 * `_fbp`, the browser's. Production only (VITE_META_PIXEL_ID), and only while game stats are on
 * (Settings → Game stats): no consent prompt yet, as for PostHog during the playtest (D24, D58).
 */
import { statsEnabled, subscribeStats } from './analytics';

const PIXEL_ID = import.meta.env.VITE_META_PIXEL_ID as string | undefined;
/** Settings → Game stats turns it off too, so the switch shows with it. */
export const metaConfigured = Boolean(PIXEL_ID);
/** As long as Meta's pixel keeps its own cookies. */
const COOKIE_SECONDS = 90 * 86_400;
const FBCLID = /^[\w.-]{1,500}$/;

type Fbq = ((...args: unknown[]) => void) & {
  callMethod?: (...args: unknown[]) => void;
  queue: unknown[];
  push: Fbq;
  loaded: boolean;
  version: string;
};

declare global {
  interface Window {
    fbq?: Fbq;
    _fbq?: Fbq;
  }
}

function cookie(name: string): string | null {
  return document.cookie.match(new RegExp(`(?:^|; )${name}=([^;]*)`))?.[1] ?? null;
}

function setCookie(name: string, value: string): void {
  document.cookie = `${name}=${value}; max-age=${COOKIE_SECONDS}; path=/; SameSite=Lax${location.protocol === 'https:' ? '; Secure' : ''}`;
}

/**
 * At boot, before anything changes the address: a link from an ad carries `fbclid`, which becomes
 * the `_fbc` cookie as Meta's pixel writes it; a browser without an `_fbp` id gets one (the pixel
 * then keeps it). Both reach the game server even where a blocker stops the pixel.
 */
export function captureMetaIds(): void {
  if (!PIXEL_ID || !statsEnabled()) return;
  const now = Date.now();
  const fbclid = new URLSearchParams(location.search).get('fbclid');
  if (fbclid && FBCLID.test(fbclid) && !cookie('_fbc')?.endsWith(`.${fbclid}`))
    setCookie('_fbc', `fb.1.${now}.${fbclid}`);
  if (!cookie('_fbp')) setCookie('_fbp', `fb.1.${now}.${Math.floor(Math.random() * 1e10)}`);
}

/** What the game server sends Meta along with its events; undefined while game stats are off. */
export function metaIds(): { fbc?: string; fbp?: string } | undefined {
  if (!PIXEL_ID || !statsEnabled()) return undefined;
  const fbc = cookie('_fbc');
  const fbp = cookie('_fbp');
  return { ...(fbc ? { fbc } : {}), ...(fbp ? { fbp } : {}) };
}

/** Meta's snippet: calls wait in a queue until fbevents.js has loaded. */
function loadPixel(id: string): void {
  if (window.fbq) return;
  const fbq = ((...args: unknown[]) => {
    if (fbq.callMethod) fbq.callMethod(...args);
    else fbq.queue.push(args);
  }) as Fbq;
  fbq.push = fbq;
  fbq.loaded = true;
  fbq.version = '2.0';
  fbq.queue = [];
  window.fbq = window._fbq = fbq;
  const script = document.createElement('script');
  script.async = true;
  script.src = 'https://connect.facebook.net/en_US/fbevents.js';
  document.head.appendChild(script);
  // Only what we send: no automatic events from buttons or page details.
  fbq('set', 'autoConfig', false, id);
  fbq('init', id);
  fbq('track', 'PageView');
}

/** The pixel and this visit's page view while game stats are on; turned off, it stops sending. */
export function startMeta(): void {
  const id = PIXEL_ID;
  if (!id) return;
  const apply = () => {
    if (!statsEnabled()) {
      window.fbq?.('consent', 'revoke');
      return;
    }
    captureMetaIds();
    if (window.fbq) window.fbq('consent', 'grant');
    else loadPixel(id);
  };
  apply();
  subscribeStats(apply);
}
